import * as React from "react";

import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { FormLabel } from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(isSelected, theme) {
  return {
    fontWeight: isSelected
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

/**
 * MultiSelectDropdown - A configurable multi-select dropdown component
 * @param {Object} props - Component props
 * @param {string} props.type - Type of multi-select ('default', 'chip', or 'checkbox')
 * @param {string} props.label - Label for the dropdown
 * @param {Array} props.options - Array of options to display (strings or objects)
 * @param {Array} props.value - Currently selected values (strings or object ids)
 * @param {Function} props.onChange - Function to call when selection changes
 * @param {Object} props.sx - Additional styling for the FormControl
 * @param {string} props.placeholder - Placeholder text when no options are selected
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {string} props.id - ID for the dropdown (optional)
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.labelKey - Key to use for display label when options are objects (default: 'label')
 * @param {string} props.valueKey - Key to use for value when options are objects (default: 'id')
 * @returns {React.ReactElement}
 */
const MultiSelectDropdown = ({
  type = "default",
  label = "Select",
  options = [],
  value = [],
  name,
  onBlur,
  onChange,
  sx = {},
  placeholder = "Select items",
  disabled = false,
  id = `multi-select-${label.toLowerCase().replace(/\s/g, "-")}`,
  required = false,
  labelKey = "label",
  valueKey = "id",
}) => {
  const theme = useTheme();
  const isObjectArray = options.length > 0 && typeof options[0] === "object";

  // Get the label for a given value
  const getOptionLabel = (optionValue) => {
    if (!isObjectArray) return optionValue;

    const option = options.find(
      (opt) => String(opt[valueKey]) === String(optionValue)
    );
    return option ? option[labelKey] : optionValue;
  };

  // Check if a value is selected
  const isValueSelected = (optionValue) => {
    if (!isObjectArray) {
      return value.includes(optionValue);
    }

    const valueToCheck = isObjectArray
      ? String(optionValue[valueKey])
      : optionValue;

    // TODO: remove later, below is fix for services auto select by default

    // return (value.length > 0) ? value.some(
    //   (val) => String(isObjectArray ? val : val) === String(valueToCheck)
    // ) : [];

    return value.some(
      (val) => String(isObjectArray ? val : val) === String(valueToCheck)
    )
  };

  const handleChange = (event) => {
    const {
      target: { value: newValue },
    } = event;

    // Convert string value to array if needed (for autofill)
    const processedValue =
      typeof newValue === "string" ? newValue.split(",") : newValue;

    // Call the provided onChange handler with the new value
    if (onChange) {
      onChange(processedValue);
    }
  };

  // Render different value displays based on type
  const renderValue = (selected) => {
    if (selected.length === 0) {
      return <span style={{ color: "darkgrey" }}>{placeholder}</span>;
    }

    // Get labels for selected values
    const selectedLabels = selected.map((val) => getOptionLabel(val));

    switch (type) {
      case "chip":
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selectedLabels.map((label, index) => (
              <Chip key={index} label={label} />
            ))}
          </Box>
        );
      case "checkbox":
        return selectedLabels.join(", ");
      default:
        return selectedLabels.join(", ");
    }
  };

  // Render menu items based on type
  const renderMenuItem = (option) => {
    const optionValue = isObjectArray ? option[valueKey] : option;
    const optionLabel = isObjectArray ? option[labelKey] : option;
    const isSelected = isValueSelected(option);

    switch (type) {
      case "checkbox":
        return (
          <MenuItem
            key={optionValue}
            value={optionValue}
            sx={{
              backgroundColor: isSelected ? "#1264e20d !important" : "white",
              "&.Mui-selected": {
                backgroundColor: "#1264e20d !important", // Apply directly to selected MenuItem
                border: "1px solid #A6C4F4",
              },
              "&.Mui-selected:hover": {
                backgroundColor: "#1264e220 !important", // Slightly darker on hover
              },
            }}
          >
            <Checkbox color="secondary" checked={isSelected} />
            <ListItemText
              primary={optionLabel}
              primaryTypographyProps={{
                noWrap: true,
                sx: {
                  color: "#323232",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: "0.875rem",
                },
              }}
            />
          </MenuItem>
        );
      case "chip":
      case "default":
      default:
        return (
          <MenuItem
            key={optionValue}
            value={optionValue}
            style={getStyles(isSelected, theme)}
          >
            {optionLabel}
          </MenuItem>
        );
    }
  };

  return (
    <FormControl sx={sx} disabled={disabled}>
      <FormLabel
        htmlFor={id}
        required={required}
        sx={{
          color: "#000000", // Ensure the default color is black
          "&.Mui-focused": {
            color: "#000000 !important", // Override MUI's default focus behavior
          },
        }}
      >
        {label}
      </FormLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        multiple
        IconComponent={() => (
          <ExpandMoreIcon
            sx={{
              position: "absolute",
              right: "10px",
              fontSize: "19px !important",
              color: "#0000008a",
            }}
          />
        )}
        name={name}
        onBlur={onBlur}
        value={value}
        onChange={handleChange}
        renderValue={renderValue}
        MenuProps={MenuProps}
        displayEmpty={Boolean(placeholder)}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "2px !important",
            fontSize: "small !important",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: "2px !important",
            fontSize: "small !important",
          },
          "& .MuiSelect-select": {
            borderRadius: "2px !important",
            fontSize: "small !important",
            display: "block !important",
          },
          "& .MuiSelect-icon": {
            borderRadius: "2px !important",
            fontSize: "small !important",
          },
          "& .css-mlog3m-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input":
            {
              borderRadius: "2px !important",
              fontSize: "small !important",
              display: "block !important",
            },
        }}
      >
        {options.map((option) => renderMenuItem(option))}
      </Select>
    </FormControl>
  );
};

export default MultiSelectDropdown;
